import { Check, X, Clock, Paperclip } from 'lucide-react';
import { Badge, Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@employer-on-record/ui';
import { formatDateRange, formatDateShort, formatNumber, type LeaveRequestDTO } from '@employer-on-record/shared';
import { Avatar } from '@/components/Avatar';
import { TONE_BY_LEAVE_STATUS, LABEL_BY_LEAVE_TYPE, cn } from '@/lib/utils';

const TONE_BY_LEAVE_TYPE = {
	annual: 'blue',
	sick: 'red',
	personal: 'violet',
	maternity: 'amber',
	paternity: 'amber',
	unpaid: 'gray'
} as const;

interface Props {
	open: boolean;
	onClose: () => void;
	request: LeaveRequestDTO | null;
}

export function LeaveDetailModal({ open, onClose, request }: Props) {
	if (!request) return null;
	const typeKey = request.type as keyof typeof LABEL_BY_LEAVE_TYPE;
	const sortedApprovals = [...(request.approvals ?? [])].sort((a, b) => a.level - b.level);

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-[560px] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Leave request</DialogTitle>
					<DialogDescription>Submitted {formatDateShort(request.createdAt)}</DialogDescription>
				</DialogHeader>

				<div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
					{request.employee && (
						<div className="flex items-center gap-3 pb-4 border-b">
							<Avatar name={request.employee.fullName} />
							<div>
								<div className="font-semibold text-[14px]">{request.employee.fullName}</div>
								<div className="text-[11px] text-muted-foreground">{request.employee.department || '—'}</div>
							</div>
						</div>
					)}

					<div className="grid grid-cols-2 gap-x-5 gap-y-3.5">
						<DetailField label="Type">
							<Badge tone={TONE_BY_LEAVE_TYPE[typeKey]}>{LABEL_BY_LEAVE_TYPE[typeKey]}</Badge>
						</DetailField>
						<DetailField label="Status">
							<Badge tone={TONE_BY_LEAVE_STATUS[request.status]} noDot>{request.status}</Badge>
						</DetailField>
						<DetailField label="Date range">
							<div className="text-[13px] font-medium">{formatDateRange(request.startDate, request.endDate)}</div>
						</DetailField>
						<DetailField label="Total days">
							<div className="text-[13px] font-medium tabular">{formatNumber(request.days)} day{Number(request.days) === 1 ? '' : 's'}</div>
						</DetailField>
					</div>

					{request.reason && (
						<DetailField label="Reason">
							<div className="text-[13px] whitespace-pre-wrap bg-background p-3 rounded-md border">{request.reason}</div>
						</DetailField>
					)}

					{request.attachmentUrl && (
						<DetailField label="Attachment">
							<a href={request.attachmentUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[12px] text-primary hover:underline">
								<Paperclip className="h-3 w-3" /> View attachment
							</a>
						</DetailField>
					)}

					{request.status === 'rejected' && request.rejectionReason && (
						<div className="border border-destructive/30 bg-red-50 rounded-md p-3.5">
							<div className="text-[11px] uppercase tracking-wider text-red-700 font-semibold mb-1">Rejection reason</div>
							<div className="text-[13px] text-red-800 whitespace-pre-wrap">{request.rejectionReason}</div>
						</div>
					)}

					<div>
						<div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Approval timeline</div>
						{sortedApprovals.length === 0 ? (
							<div className="text-[12px] text-muted-foreground italic">No approvers configured.</div>
						) : (
							<ol className="relative space-y-2.5 pl-6">
								{sortedApprovals.map((step) => {
									const isCurrent = step.status === 'pending' && step.level === request.currentLevel;
									const Icon = step.status === 'approved' ? Check : step.status === 'rejected' ? X : Clock;
									const dotClass =
										step.status === 'approved' ? 'bg-success text-white border-success' :
										step.status === 'rejected' ? 'bg-destructive text-white border-destructive' :
										isCurrent ? 'bg-warning text-white border-warning' :
										'bg-white text-muted-foreground border-border';
									return (
										<li key={step.id} className="relative">
											<span className={cn('absolute -left-6 top-0 h-5 w-5 rounded-full grid place-items-center border', dotClass)}>
												<Icon className="h-3 w-3" />
											</span>
											<div className="flex items-baseline justify-between gap-3">
												<div>
													<div className="text-[13px] font-medium">L{step.level} · {step.approverName ?? 'Unassigned'}</div>
													<div className="text-[11px] text-muted-foreground capitalize">{isCurrent ? 'Awaiting decision' : step.status}{step.decidedAt && ` · ${formatDateShort(step.decidedAt)}`}</div>
												</div>
											</div>
											{step.note && (
												<div className="mt-1 text-[12px] text-muted-foreground italic">"{step.note}"</div>
											)}
										</li>
									);
								})}
							</ol>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button variant="secondary" onClick={onClose}>Close</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div>
			<div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">{label}</div>
			{children}
		</div>
	);
}
